import { useCallback, useEffect, useMemo, useRef } from "react"

import { useAgentNamesTransport } from "@/agents/transport"
import { useBaselineDetail, useBaselineHistory } from "@/metrics/baselines"
import {
  buildMetricDashboardSourcesById,
  useMetricsList,
} from "@/metrics/catalog"
import { useMetricDirectory } from "@/metrics/registry"

import type { WidgetExportData } from "../../components/BaseWidget"
import { useDashboardFilters } from "../../contexts/DashboardFiltersContext"
import { useDashboardBatchTransportQuery } from "../../routes/dashboardBatchTransportQuery"
import type { ChartWidgetConfig, StatisticDataResult } from "../../types/chart"
import { AGGREGATION_TYPE, VISUALIZATION_TYPE } from "../../types/core"
import {
  getChartAgentIds,
  resolveChartTargetCondition,
  type DashboardChartRow,
} from "../../utils/chart/chartWidgetDataState"
import {
  buildChartData,
  buildDashboardMetricQueryInput,
  buildDashboardStatisticQueryInput,
  buildHumanReviewMetricQueryInput,
  createChartBaselineData,
  createChartExportDataKey,
  getChartBucketInterval,
  getChartQueryResult,
  getChartTargetValues,
  getChartZoomOverride,
  getEffectiveChartDateRange,
  getIsWholeRangeBarVisualization,
  hasChartSuccessCriteriaError,
  isMetricBinary,
  resolveChartMetric,
  shouldEnableFlexibleChartQuery,
  shouldEnableHumanReviewChartQuery,
  shouldEnableStatisticChartQuery,
} from "../../utils/chart/chartWidgetState"
import {
  mergeAgentNameLookups,
  useDisplayNameLookups,
} from "../../utils/chart/displayNameUtils"
import { calculateSplitKeys } from "../../utils/chart/splitKeys"
import { getBrowserTimezone } from "../../utils/chart/timezoneUtils"
import {
  isHumanReviewSource,
  isUploadedConversationSource,
  resolveDataSource,
} from "../../utils/dataSource"
import {
  getDashboardBatchRows,
  isTransportDashboardQueryLoading,
  serializeDashboardBatchQuery,
} from "../../utils/transportDashboardQueries"
import { useGlobalDateRangeOverride } from "../useGlobalDateRangeOverride"

interface UseChartWidgetDataParams {
  config: ChartWidgetConfig
  onExportDataReady?: (data: WidgetExportData | null) => void
  onSplitKeysChange?: (splitKeys: string[]) => void
}

const CHART_QUERY_STALE_TIME = 5 * 60 * 1000
const CHART_QUERY_GC_TIME = 15 * 60 * 1000

export const useChartWidgetData = ({
  config,
  onExportDataReady,
  onSplitKeysChange,
}: UseChartWidgetDataParams) => {
  const { state } = useDashboardFilters()
  const { lookups, isLoading: lookupsLoading } = useDisplayNameLookups()
  const { setCustomGlobalOverride } = useGlobalDateRangeOverride()
  const {
    dateRange: globalDateRange,
    globalDateRangeOverride,
    isOverrideActive,
  } = state

  const handleChartZoom = useCallback(
    (range: { start: Date; end: Date }) => {
      setCustomGlobalOverride(
        getChartZoomOverride(range, config.bucketInterval),
      )
    },
    [setCustomGlobalOverride, config.bucketInterval],
  )

  const { data: allMetricRecords, isLoading: isMetricLoading } =
    useMetricsList()
  const dashboardMetrics = useMemo(
    () => buildMetricDashboardSourcesById(allMetricRecords),
    [allMetricRecords],
  )

  const metric = useMemo(
    () => resolveChartMetric(dashboardMetrics, config.metricId),
    [dashboardMetrics, config.metricId],
  )
  const { byManager } = useMetricDirectory()
  const metricIsBinary = useMemo(
    () => isMetricBinary(metric, byManager[metric?.metric_manager ?? ""]),
    [metric, byManager],
  )

  const effectiveTargetCondition = useMemo(
    () => resolveChartTargetCondition(metric, metricIsBinary),
    [metric, metricIsBinary],
  )

  const effectiveDateRange = useMemo(
    () =>
      getEffectiveChartDateRange({
        globalDateRange,
        globalDateRangeOverride,
        isOverrideActive,
      }),
    [globalDateRange, globalDateRangeOverride, isOverrideActive],
  )

  const bucketInterval = getChartBucketInterval(config.bucketInterval)
  const timezone = getBrowserTimezone()

  const baselineBandsEnabled = Boolean(
    config.showBaselineBands &&
    config.baselineId &&
    config.aggregation === AGGREGATION_TYPE.AVG,
  )

  const { data: baselineRecord } = useBaselineDetail(
    config.metricId,
    config.baselineId,
    {
      enabled: baselineBandsEnabled,
      staleTime: CHART_QUERY_STALE_TIME,
      gcTime: CHART_QUERY_GC_TIME,
    },
  )

  const { data: baselineHistory } = useBaselineHistory(
    {
      baselineId: config.baselineId ?? "",
      bucketInterval,
      timezone,
      startDate: effectiveDateRange.start.toISOString(),
      endDate: effectiveDateRange.end.toISOString(),
    },
    {
      enabled: baselineBandsEnabled,
      staleTime: CHART_QUERY_STALE_TIME,
      gcTime: CHART_QUERY_GC_TIME,
    },
  )

  const baselineData = useMemo(
    () =>
      createChartBaselineData({
        baselineRecord: baselineRecord
          ? {
              ...baselineRecord,
              baseline_float: baselineRecord.baseline_float ?? null,
              baseline_sigma: baselineRecord.baseline_sigma ?? null,
            }
          : undefined,
        baselineHistory,
        baselineDirection: config.baselineDirection,
      }),
    [baselineRecord, baselineHistory, config.baselineDirection],
  )

  const isHistogramVisualization =
    config.visualizationType === VISUALIZATION_TYPE.HISTOGRAM
  const isTopListVisualization =
    config.visualizationType === VISUALIZATION_TYPE.TOP_LIST
  const isWholeRangeVisualization = getIsWholeRangeBarVisualization(config)
  const isStatisticVisualization =
    config.visualizationType === VISUALIZATION_TYPE.STATISTIC
  const dataSource = resolveDataSource(config)
  const isMonitoring = isUploadedConversationSource(dataSource)
  const isHumanReview = isHumanReviewSource(dataSource)

  const flexibleQueryEnabled = shouldEnableFlexibleChartQuery({
    aggregation: config.aggregation,
    effectiveDateRange,
    effectiveTargetCondition,
    isHumanReview,
    metricId: config.metricId,
    metricIsBinary,
    metricOutputType: config.metricOutputType,
  })
  const statisticQueryEnabled = shouldEnableStatisticChartQuery({
    effectiveDateRange,
    isHumanReview,
    isStatisticVisualization,
    metricId: config.metricId,
  })
  const humanReviewQueryEnabled = shouldEnableHumanReviewChartQuery({
    effectiveDateRange,
    isHumanReview,
    metricId: config.metricId,
  })

  const flexibleQueryRequest = useMemo(
    () => ({
      queries: [
        serializeDashboardBatchQuery({
          query_id: "flexible",
          kind: "queryDashboardMetric",
          ...buildDashboardMetricQueryInput({
            bucketInterval,
            config,
            effectiveDateRange,
            effectiveTargetCondition,
            isHistogramVisualization,
            isMonitoring,
            isTopListVisualization,
            isWholeRangeVisualization,
            metricId: metric?.id,
            timezone,
          }),
        }),
      ],
    }),
    [
      bucketInterval,
      config,
      effectiveDateRange,
      effectiveTargetCondition,
      isHistogramVisualization,
      isMonitoring,
      isTopListVisualization,
      isWholeRangeVisualization,
      metric?.id,
      timezone,
    ],
  )

  const statisticQueryRequest = useMemo(
    () => ({
      queries: [
        serializeDashboardBatchQuery({
          query_id: "statistic",
          kind: "queryDashboardStatistic",
          ...buildDashboardStatisticQueryInput({
            config,
            effectiveDateRange,
            effectiveTargetCondition,
            isMonitoring,
            metricId: metric?.id,
          }),
        }),
      ],
    }),
    [
      config,
      effectiveDateRange,
      effectiveTargetCondition,
      isMonitoring,
      metric?.id,
    ],
  )

  const humanReviewQueryRequest = useMemo(
    () => ({
      queries: [
        serializeDashboardBatchQuery({
          query_id: "human_review",
          kind: "queryHumanReviewMetric",
          ...buildHumanReviewMetricQueryInput({
            bucketInterval,
            config,
            effectiveDateRange,
            timezone,
          }),
        }),
      ],
    }),
    [bucketInterval, config, effectiveDateRange, timezone],
  )

  const flexibleQuery = useDashboardBatchTransportQuery({
    request: flexibleQueryRequest,
    options: {
      enabled: flexibleQueryEnabled,
      staleTime: CHART_QUERY_STALE_TIME,
      gcTime: CHART_QUERY_GC_TIME,
    },
  })

  const statisticQuery = useDashboardBatchTransportQuery({
    request: statisticQueryRequest,
    options: {
      enabled: statisticQueryEnabled,
      staleTime: CHART_QUERY_STALE_TIME,
      gcTime: CHART_QUERY_GC_TIME,
    },
  })

  const humanReviewQuery = useDashboardBatchTransportQuery({
    request: humanReviewQueryRequest,
    options: {
      enabled: humanReviewQueryEnabled,
      staleTime: CHART_QUERY_STALE_TIME,
      gcTime: CHART_QUERY_GC_TIME,
    },
  })

  const flexibleRows = useMemo(
    () => getDashboardBatchRows<DashboardChartRow>(flexibleQuery.data),
    [flexibleQuery.data],
  )
  const statisticRows = useMemo(
    () => getDashboardBatchRows<StatisticDataResult>(statisticQuery.data),
    [statisticQuery.data],
  )
  const humanReviewRows = useMemo(
    () => getDashboardBatchRows<DashboardChartRow>(humanReviewQuery.data),
    [humanReviewQuery.data],
  )

  const humanReviewData = useMemo(() => {
    if (!isHumanReview || humanReviewRows.length === 0) {
      return null
    }
    return humanReviewRows
  }, [humanReviewRows, isHumanReview])

  const { data, error, isLoading, statisticData } = getChartQueryResult({
    flexibleError: flexibleQuery.error,
    flexibleLoading: isTransportDashboardQueryLoading(
      flexibleQuery,
      flexibleQueryEnabled,
    ),
    flexibleQueryData: flexibleRows,
    humanReviewData,
    humanReviewError: humanReviewQuery.error,
    humanReviewLoading: isTransportDashboardQueryLoading(
      humanReviewQuery,
      humanReviewQueryEnabled,
    ),
    isHumanReview,
    isStatisticVisualization,
    statisticData: statisticRows,
    statisticError: statisticQuery.error,
    statisticLoading: isTransportDashboardQueryLoading(
      statisticQuery,
      statisticQueryEnabled,
    ),
  })

  const uniqueAgentIds = useMemo(
    () =>
      getChartAgentIds({
        dimension: config.groupBy || config.splitBy,
        flexibleRows: data,
        statisticRows,
      }),
    [config.groupBy, config.splitBy, data, statisticRows],
  )

  const { data: directAgentNames, isLoading: isDirectAgentNamesLoading } =
    useAgentNamesTransport(uniqueAgentIds)

  const displayNameLookups = useMemo(
    () => mergeAgentNameLookups(lookups, directAgentNames),
    [directAgentNames, lookups],
  )

  const chartData = useMemo(
    () =>
      buildChartData({
        bucketInterval,
        config,
        data,
        effectiveDateRange,
        isHistogramVisualization,
        isTopListVisualization,
        isWholeRangeVisualization,
      }),
    [
      bucketInterval,
      config,
      data,
      effectiveDateRange,
      isHistogramVisualization,
      isTopListVisualization,
      isWholeRangeVisualization,
    ],
  )

  const targetValues = useMemo(() => getChartTargetValues(metric), [metric])
  const splitKeys = useMemo(
    () => calculateSplitKeys(chartData, config, targetValues),
    [chartData, config, targetValues],
  )

  const prevSplitKeysRef = useRef<string[]>([])
  useEffect(() => {
    if (!onSplitKeysChange) {
      return
    }

    const previousSplitKeys = prevSplitKeysRef.current
    const hasChanged =
      splitKeys.length !== previousSplitKeys.length ||
      splitKeys.some((key, index) => key !== previousSplitKeys[index])

    if (!hasChanged) {
      return
    }

    prevSplitKeysRef.current = splitKeys
    onSplitKeysChange(splitKeys)
  }, [splitKeys, onSplitKeysChange])

  const stableDateRange = useMemo(
    () => ({
      start: effectiveDateRange.start,
      end: effectiveDateRange.end,
    }),
    [effectiveDateRange],
  )

  const exportDataKey = useMemo(
    () =>
      createChartExportDataKey({
        chartData,
        splitKeys,
        lookups: displayNameLookups,
        stableDateRange,
      }),
    [chartData, splitKeys, displayNameLookups, stableDateRange],
  )

  const prevExportDataRef = useRef("")
  useEffect(() => {
    if (!onExportDataReady) {
      return
    }

    if (prevExportDataRef.current === exportDataKey) {
      return
    }

    prevExportDataRef.current = exportDataKey
    if (chartData.length === 0) {
      onExportDataReady(null)
      return
    }

    onExportDataReady({
      widgetType: "chart",
      chartData,
      splitKeys,
      displayNameLookups,
      dateRange: stableDateRange,
    })
  }, [
    chartData,
    displayNameLookups,
    exportDataKey,
    onExportDataReady,
    splitKeys,
    stableDateRange,
  ])

  const showSuccessCriteriaError = hasChartSuccessCriteriaError({
    config,
    effectiveTargetCondition,
    isMetricLoading,
    metricIsBinary,
  })

  return {
    chartData,
    data,
    error,
    handleChartZoom,
    isLoading,
    isMetricLoading,
    lookups: displayNameLookups,
    lookupsLoading: lookupsLoading || isDirectAgentNamesLoading,
    metric,
    showSuccessCriteriaError,
    splitKeys,
    stableDateRange,
    baselineData,
    statisticData,
  }
}
